/**
 * Card-feed mocks — temporary scaffolding while the backend stabilizes.
 *
 * FIXME(card-feed-mocks): remove this file and the `if (MOCK_CARD_FEEDS_ENABLED)`
 * branches in the three domain hooks (`useQueryCardFeeds`, `useQueryCardFeed`,
 * `useQueryCardFeedCards`) once the real card-feeds endpoints are stable.
 *
 * Flip MOCK_CARD_FEEDS_ENABLED to `true` locally to develop against the mock
 * data. Default ships `false` so production never serves mocks. Do not commit
 * a flipped value.
 */
import {
  CardFeedAssignedUser,
  CardFeedEntriesResponse,
  CardFeedEntryResponse,
  CardFeedMembershipStatus,
  CardFeedResponse,
} from 'src/queries/cardFeeds/cardFeeds.models'

export const MOCK_CARD_FEEDS_ENABLED = false

// Builds a slim assigned-user mock matching the API shape. An avatarUrl yields
// the photo path in UserAvatar; without one it falls back to initials.
// membershipStatus defaults to ACTIVE (current member).
let mockUserSeq = 0
const mockAssignedUser = (
  firstName: string | null,
  lastName: string | null,
  email: string,
  avatarUrl?: string,
  membershipStatus: CardFeedMembershipStatus = 'ACTIVE',
): CardFeedAssignedUser => ({
  id: `u_mock_${(mockUserSeq += 1)}`,
  firstName,
  lastName,
  email,
  avatarType: avatarUrl ? 'GRAVATAR' : 'DEFAULT',
  avatarUrl,
  organizationId: 'org_mock',
  organizationRole: 'MEMBER',
  membershipStatus,
})

export const MOCK_CARD_FEEDS: CardFeedResponse[] = [
  {
    id: 'feed_mock_active_visa',
    organizationId: 'org_mock',
    cardProgramName: 'Acme Corporate Visa',
    cardNetwork: 'VISA',
    integratorFeedId: 'INT-VISA-001',
    cardCount: 42,
    status: 'ACTIVE',
    referenceId: 'REF-001',
    createdAt: '2026-04-02T10:15:00Z',
  },
  {
    id: 'feed_mock_active_mc',
    organizationId: 'org_mock',
    cardProgramName: 'Globex Mastercard Travel',
    cardNetwork: 'MASTERCARD',
    integratorFeedId: 'INT-MC-014',
    cardCount: 18,
    status: 'ACTIVE',
    referenceId: 'REF-002',
    createdAt: '2026-03-11T14:42:00Z',
  },
  {
    id: 'feed_mock_pending_amex',
    organizationId: 'org_mock',
    cardProgramName: 'Initech AmEx Platinum',
    cardNetwork: 'AMERICAN_EXPRESS',
    integratorFeedId: 'INT-AMEX-007',
    cardCount: 0,
    status: 'SUBMITTED',
    referenceId: 'REF-003',
    createdAt: '2026-05-22T08:00:00Z',
  },
  {
    id: 'feed_mock_pending_visa',
    organizationId: 'org_mock',
    cardProgramName: 'Soylent Visa Purchasing',
    cardNetwork: 'VISA',
    integratorFeedId: 'INT-VISA-022',
    cardCount: 0,
    status: 'SUBMITTED',
    referenceId: 'REF-004',
    createdAt: '2026-05-24T16:20:00Z',
  },
  {
    id: 'feed_mock_disabled_mc',
    organizationId: 'org_mock',
    cardProgramName: 'Hooli Mastercard (Legacy)',
    cardNetwork: 'MASTERCARD',
    integratorFeedId: 'INT-MC-003',
    cardCount: 7,
    status: 'DEACTIVATED',
    referenceId: 'REF-005',
    createdAt: '2025-11-05T09:00:00Z',
  },
  {
    id: 'feed_mock_failed_visa',
    organizationId: 'org_mock',
    // Empty name on a non-pending feed — exercises the name column "—" fallback.
    cardProgramName: '',
    cardNetwork: 'VISA',
    integratorFeedId: 'INT-VISA-099',
    cardCount: 0,
    status: 'FAILED',
    referenceId: 'REF-006',
    createdAt: '2026-05-23T07:00:00Z',
  },
]

// For ids that aren't in MOCK_CARD_FEEDS (e.g., deep-link to an arbitrary id),
// return a synthesized feed so the detail page still renders rather than
// 404-redirecting through the mock layer.
export const synthesizeMockFeed = (
  organizationId: string,
  cardFeedId: string,
): CardFeedResponse => ({
  id: cardFeedId,
  organizationId,
  cardProgramName: 'Mock Card Feed',
  cardNetwork: 'VISA',
  integratorFeedId: 'INT-MOCK',
  cardCount: 0,
  status: 'ACTIVE',
  referenceId: 'REF-MOCK',
  createdAt: '2026-05-26T00:00:00Z',
})

const MOCK_CARD_ENTRIES: CardFeedEntryResponse[] = [
  {
    id: 'card_mock_01',
    displayName: 'Operations - East',
    last4: '1042',
    // One entry carries an avatarUrl to exercise the photo path in UserAvatar.
    assignedUser: mockAssignedUser(
      'Avery',
      'Chen',
      'avery.chen@example.com',
      'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=identicon',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-23T09:00:00Z',
  },
  {
    id: 'card_mock_02',
    displayName: 'Operations - West',
    last4: '1043',
    assignedUser: mockAssignedUser(
      'Brandon',
      'Diaz',
      'brandon.diaz@example.com',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-22T11:15:00Z',
  },
  {
    id: 'card_mock_03',
    displayName: 'Marketing - Brand',
    last4: '2018',
    assignedUser: mockAssignedUser('Casey', 'Patel', 'casey.patel@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-05-20T14:30:00Z',
  },
  {
    id: 'card_mock_04',
    displayName: 'Marketing - Growth',
    last4: '2019',
    status: 'UNASSIGNED',
    createdAt: '2026-05-20T14:30:00Z',
  },
  {
    id: 'card_mock_05',
    displayName: 'Engineering - Platform',
    last4: '3301',
    // A removed member — exercises the deactivated (greyed) avatar treatment.
    assignedUser: mockAssignedUser(
      'Dana',
      'Kim',
      'dana.kim@example.com',
      undefined,
      'DEACTIVATED',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-18T10:00:00Z',
  },
  {
    id: 'card_mock_06',
    displayName: 'Engineering - Mobile',
    last4: '3302',
    status: 'UNASSIGNED',
    createdAt: '2026-05-18T10:05:00Z',
  },
  {
    id: 'card_mock_07',
    displayName: 'Engineering - Data',
    last4: '3303',
    status: 'UNASSIGNED',
    createdAt: '2026-05-18T10:10:00Z',
  },
  {
    id: 'card_mock_08',
    displayName: 'Sales - Enterprise',
    last4: '4410',
    assignedUser: mockAssignedUser('Elena', 'Ramos', 'elena.ramos@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-05-15T08:45:00Z',
  },
  {
    id: 'card_mock_09',
    displayName: 'Sales - SMB',
    last4: '4411',
    assignedUser: mockAssignedUser(
      'Felix',
      'Okafor',
      'felix.okafor@example.com',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-15T08:50:00Z',
  },
  {
    id: 'card_mock_10',
    displayName: 'Sales - Partnerships',
    last4: '4412',
    status: 'UNASSIGNED',
    createdAt: '2026-05-15T08:55:00Z',
  },
  {
    id: 'card_mock_11',
    displayName: 'Finance - AP',
    last4: '5520',
    assignedUser: mockAssignedUser(
      'Gabriela',
      'Souza',
      'gabriela.souza@example.com',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-12T16:00:00Z',
  },
  {
    id: 'card_mock_12',
    displayName: 'Finance - Treasury',
    last4: '5521',
    assignedUser: mockAssignedUser('Harish', 'Iyer', 'harish.iyer@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-05-12T16:05:00Z',
  },
  {
    id: 'card_mock_13',
    displayName: 'People - Recruiting',
    last4: '6630',
    status: 'UNASSIGNED',
    createdAt: '2026-05-10T09:30:00Z',
  },
  {
    id: 'card_mock_14',
    displayName: 'People - Onboarding',
    last4: '6631',
    assignedUser: mockAssignedUser(
      'Imani',
      'Brooks',
      'imani.brooks@example.com',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-10T09:35:00Z',
  },
  {
    id: 'card_mock_15',
    displayName: 'IT - Helpdesk',
    last4: '7740',
    status: 'UNASSIGNED',
    createdAt: '2026-05-08T12:00:00Z',
  },
  {
    id: 'card_mock_16',
    displayName: 'IT - Infrastructure',
    last4: '7741',
    assignedUser: mockAssignedUser(
      'Jordan',
      'Wagner',
      'jordan.wagner@example.com',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-05-08T12:05:00Z',
  },
  {
    id: 'card_mock_17',
    displayName: 'Legal - Contracts',
    last4: '8850',
    assignedUser: mockAssignedUser('Kavya', 'Nair', 'kavya.nair@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-05-05T11:20:00Z',
  },
  {
    id: 'card_mock_18',
    displayName: 'Legal - Compliance',
    last4: '8851',
    status: 'UNASSIGNED',
    createdAt: '2026-05-05T11:25:00Z',
  },
  {
    id: 'card_mock_19',
    displayName: 'Product - Research',
    last4: '9960',
    assignedUser: mockAssignedUser('Liam', 'Foster', 'liam.foster@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-05-02T15:00:00Z',
  },
  {
    id: 'card_mock_20',
    displayName: 'Product - Design',
    last4: '9961',
    status: 'UNASSIGNED',
    createdAt: '2026-05-02T15:05:00Z',
  },
  {
    id: 'card_mock_21',
    displayName: 'Customer Success - East',
    last4: '1170',
    assignedUser: mockAssignedUser('Mei', 'Tanaka', 'mei.tanaka@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-04-28T10:00:00Z',
  },
  {
    id: 'card_mock_22',
    displayName: 'Customer Success - West',
    last4: '1171',
    assignedUser: mockAssignedUser(
      'Noah',
      'Bergstrom',
      'noah.bergstrom@example.com',
    ),
    status: 'ASSIGNED',
    createdAt: '2026-04-28T10:05:00Z',
  },
  {
    id: 'card_mock_23',
    displayName: 'Procurement - Hardware',
    last4: '1280',
    status: 'UNASSIGNED',
    createdAt: '2026-04-25T13:30:00Z',
  },
  {
    id: 'card_mock_24',
    displayName: 'Procurement - SaaS',
    last4: '1281',
    assignedUser: mockAssignedUser('Olivia', 'Park', 'olivia.park@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-04-25T13:35:00Z',
  },
  {
    id: 'card_mock_25',
    displayName: 'Executive - Travel',
    last4: '1390',
    assignedUser: mockAssignedUser('Priya', 'Shah', 'priya.shah@example.com'),
    status: 'ASSIGNED',
    createdAt: '2026-04-20T08:00:00Z',
  },
  // PENDING cards: an invite has been sent but not yet accepted. The assignee
  // resolves to an INVITED, email-only user (no name yet), so the table links
  // the invitee address to their detail page and the card is not bulk-selectable.
  {
    id: 'card_mock_26',
    displayName: 'Operations - South',
    last4: '1044',
    assignedUser: mockAssignedUser(
      null,
      null,
      'reese.calloway@example.com',
      undefined,
      'INVITED',
    ),
    status: 'PENDING',
    createdAt: '2026-05-25T09:00:00Z',
  },
  {
    id: 'card_mock_27',
    displayName: 'Marketing - Events',
    last4: '2020',
    assignedUser: mockAssignedUser(
      null,
      null,
      'theo.nakamura@example.com',
      undefined,
      'INVITED',
    ),
    status: 'PENDING',
    createdAt: '2026-05-25T09:05:00Z',
  },
  {
    id: 'card_mock_28',
    displayName: 'Engineering - Security',
    last4: '3304',
    assignedUser: mockAssignedUser(
      null,
      null,
      'sloane.whitaker@example.com',
      undefined,
      'INVITED',
    ),
    status: 'PENDING',
    createdAt: '2026-05-25T09:10:00Z',
  },
]

export const MOCK_CARD_ENTRIES_RESPONSE: CardFeedEntriesResponse = {
  creditCards: MOCK_CARD_ENTRIES,
  pagination: {
    page: 0,
    pageSize: 100,
    totalPages: 1,
  },
}
